"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import Button from "../common/button";
import FAIcon from "../icons/faIcon";
import FooterLocationsSection from "./footerLocationsSection";
import {
  getLocationRegionPath,
  getLocationRegions,
  getLocationTownPath,
} from "@/lib/locations";

const sectorItems = [
  {
    id: "manufacturing",
    title: "Manufacturing",
    href: "/sectors/manufacturing",
  },
  { id: "logistics", title: "Logistics", href: "/sectors/logistics" },
  { id: "construction", title: "Construction", href: "/sectors/construction" },
  { id: "engineering", title: "Engineering", href: "/sectors/engineering" },
  { id: "retail", title: "Retail", href: "/sectors/retail" },
];

const footerGroups = [
  {
    title: "Services",
    links: [
      { label: "All Services", href: "/services" },
      { label: "Health & Safety Consultancy", href: "/services/health-safety" },
      {
        label: "Outsourced Consultancy",
        href: "/services/outsourced-consultancy",
      },
      {
        label: "Occupational Hygiene",
        href: "/services/occupational-hygiene-consultancy",
      },
      {
        label: "Statutory Inspection Management",
        href: "/services/statutory-inspection-management",
      },
    ],
  },
  {
    title: "Training",
    links: [
      { label: "Training Overview", href: "/services/training" },
      { label: "Consultant-Led Training", href: "/services/training" },
      { label: "On-Site Training", href: "/services/training" },
      { label: "Courses", href: "/services/training" },
    ],
  },
  {
    title: "Sectors",
    links: sectorItems.map((sector) => ({
      label: sector.title,
      href: sector.href,
    })),
  },
  {
    title: "Company",
    links: [
      { label: "About us", href: "/about-us" },
      { label: "News", href: "/news" },
      { label: "Our Clients", href: "/our-clients" },
      { label: "Privacy & Cookies", href: "/privacy-and-cookies" },
    ],
  },
];

const footerSocials = [
  {
    icon: "youtube",
    href: "https://www.youtube.com/channel/UC90QrvQvZcljt6ZAlrUi0-w",
    label: "YouTube",
  },
  {
    icon: "facebook",
    href: "https://www.facebook.com/SafetyManagementLtd/",
    label: "Facebook",
  },
  {
    icon: "linkedin-in",
    href: "https://www.linkedin.com/company/safety-management-limited-sml/?originalSubdomain=uk",
    label: "LinkedIn",
  },
];

const footerLocationRegions = getLocationRegions().map((region) => ({
  name: region.name,
  href: getLocationRegionPath(region.slug),
  towns: region.towns.map((town) => ({
    name: town.name,
    href: getLocationTownPath(region.slug, town.slug),
  })),
}));

function FooterLink({ link }) {
  const className =
    "font-raleway text-md leading-tight  text-white/86 transition-colors duration-200 hover:text-white sm:text-md";

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link href={link.href} className={className}>
      {link.label}
    </Link>
  );
}

export default function Footer() {
  const pathname = usePathname();
  const isContactPage = pathname === "/contact";

  return (
    <footer className="relative bg-accent mt-1 m-2 rounded-lg px-2 pb-18 pt-24 text-white sm:m-4 sm:px-6 lg:px-8 ">
      <div className="mx-auto flex w-full max-w-400 flex-col gap-16 z-10">
        <div className="grid gap-14 lg:grid-cols-[1.55fr_3fr] lg:gap-20 px-10 z-10">
          <div className="max-w-sm  space-y-6">
            <div className="flex items-center gap-4 pt-2">
              <Image
                src="/images/logo/sml-logo-wide-white-svg.svg"
                alt="SML Logo"
                width={140}
                height={80}
              />
            </div>
            <p className="max-w-sm font-raleway text-md leading-tight text-white/82 sm:text-md">
              Safety Management is a UK-based consultancy providing expert
              health, safety and risk management consulting to organisations
              across a wide range of industries.
            </p>
          </div>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
            {footerGroups.map((group) => (
              <div key={group.title} className="space-y-5">
                <h3 className="font-raleway text-lg font-medium  text-primary">
                  {group.title}
                </h3>
                <div className="flex flex-col gap-4">
                  {group.links.map((link) => (
                    <FooterLink key={link.label} link={link} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col rounded-[2.2rem] border border-white/12  px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
          <Image
            src="/images/background/map.svg"
            alt="Hero Image"
            fill
            className="object-cover opacity-4 max-h-225"
            priority
          />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between z-10">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <FAIcon icon="phone" size={24} noMargin />
              <a
                href="tel:+441327311300"
                className="text-2xl sm:text-3xl font-semibold mb-0.5 transition-colors duration-200 hover:text-primary"
              >
                +44 (0)1327 311300
              </a>
            </div>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-6 text-white/82">
              <Button
                text={
                  <>
                    <span className="sm:hidden">Send us a message</span>
                    <span className="hidden sm:inline">
                      Not sure where to start? Send us a message
                    </span>
                  </>
                }
                variant="border"
                href={isContactPage ? "#contact-form" : "/contact#contact-form"}
                className="whitespace-nowrap px-5 text-base sm:px-8 sm:text-lg"
                onClick={
                  isContactPage
                    ? (e) => {
                        e.preventDefault();
                        document
                          .getElementById("contact-form")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }
                    : undefined
                }
              />
              <div className="flex gap-3">
                {footerSocials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                    className="flex items-center justify-center rounded-full transition-colors duration-200 hover:text-white"
                  >
                    <FAIcon
                      icon={social.icon}
                      type="brand"
                      noMargin
                      size={30}
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col gap-8 z-10 border-t border-transparent pt-0 lg:mt-4 lg:flex-row lg:items-end lg:justify-between">
            <p className="font-raleway text-md leading-tight tracking-[-0.03em] text-white/72 sm:text-md">
              Copyright © Safety Management Ltd 2026
            </p>
            <p className="font-raleway text-md leading-tight tracking-[-0.03em] text-white/72 sm:text-md">
              8 Cottesbrooke Park, Heartlands Business Park Daventry, Northants
              NN11 8YL
            </p>
        
          </div>
        </div>
        <FooterLocationsSection regions={footerLocationRegions} />
          
      </div>
    </footer>
  );
}
